import {ReactNode, useMemo} from "react";
import {usePersistentState} from "./usePersistentState";

export type SortDirection = 'asc' | 'desc';

export interface SortState<K extends string> {
    column: K;
    direction: SortDirection;
}

type SortValue = string | number | boolean | null | undefined;

const collator = new Intl.Collator(undefined, { numeric: true, sensitivity: 'base' });

// Empty values go last whichever way the column is sorted.
function compare(l: SortValue, r: SortValue, direction: SortDirection): number {
    const lEmpty = l === null || l === undefined || l === '';
    const rEmpty = r === null || r === undefined || r === '';
    if (lEmpty || rEmpty) {
        return lEmpty === rEmpty ? 0 : lEmpty ? 1 : -1;
    }
    const result = typeof l === 'string' && typeof r === 'string' ? collator.compare(l, r) : Number(l) - Number(r);
    return direction === 'asc' ? result : -result;
}

// Optional single-column sorting for a table. Unsorted until a header is clicked, so items keep their own order;
// clicking a column sorts it ascending, and clicking the sorted column again reverses it. The sort is remembered
// in this browser under storageKey.
export function useSort<T, K extends string>(storageKey: string, items: T[] | undefined, columns: Record<K, (item: T) => SortValue>) {
    const [sort, setSort] = usePersistentState<SortState<K> | undefined>(storageKey, undefined,
        (value): value is SortState<K> | undefined => isSortState(value, columns));

    const sorted = useMemo(() => {
        if (!items || !sort) {
            return items ?? [];
        }
        const value = columns[sort.column];
        return [...items].sort((l, r) => compare(value(l), value(r), sort.direction));
        // columns is usually an inline object: its accessors depend only on the items.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [items, sort]);

    const toggle = (column: K) => setSort(current =>
        current?.column === column ? { column, direction: current.direction === 'asc' ? 'desc' : 'asc' } : { column, direction: 'asc' });

    return { sorted, sort, toggle };
}

function isSortState(value: unknown, columns: Record<string, unknown>): boolean {
    if (typeof value !== 'object' || value === null) {
        return false;
    }
    const {column, direction} = value as { column?: unknown, direction?: unknown };
    return typeof column === 'string' && Object.hasOwn(columns, column) && (direction === 'asc' || direction === 'desc');
}

// A clickable column header showing a triangle when it's the sorted column.
export function SortHeader<K extends string>({column, sort, onSort, children, className}: {
    column: K, sort: SortState<K> | undefined, onSort(column: K): void, children: ReactNode, className?: string
}) {
    const direction = sort?.column === column ? sort.direction : undefined;
    return (
        <th className={className} aria-sort={direction === 'asc' ? 'ascending' : direction === 'desc' ? 'descending' : undefined}>
            <button type="button" className="sort-header" onClick={() => onSort(column)}>
                {children}
                {direction && <i className={`bi ${direction === 'asc' ? 'bi-caret-up-fill' : 'bi-caret-down-fill'} ms-1`} aria-hidden="true"></i>}
            </button>
        </th>
    );
}
